import React, { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { apiService } from '../services/api';
import { sanitizeHtml } from '../utils/sanitize';

const UserNotificationsPage: React.FC = () => {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const { data, refetch } = useQuery({
    queryKey: ['myNotifications', page, pageSize],
    queryFn: () => apiService.getMyNotifications(page, pageSize)
  });

  useEffect(() => { refetch(); }, [page, pageSize, refetch]);

  const items = data?.data?.data || [];
  const totalPages = data?.data?.totalPages || 1;

  const handleNotificationClick = async (notificationId: number, invoiceId?: number) => {
    try {
      await apiService.markNotificationRead(notificationId);
      refetch();
      
      if (invoiceId) {
        navigate(`/invoices/${invoiceId}/edit`);
      }
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const renderNotificationMessage = (notification: any) => {
    const message = notification.notification.message;
    
    // Check if this is an invoice notification
    if (notification.notification.notificationType === 'InvoiceOverdue' || 
        notification.notification.notificationType === 'InvoiceDueSoon') {
      
      // Extract invoice number from message (e.g., "Invoice #INV-00036 is overdue by 4 days")
      const invoiceMatch = message.match(/Invoice #([A-Z0-9-]+)/);
      if (invoiceMatch && notification.notification.invoiceId) {
        const invoiceNumber = invoiceMatch[1];
        const beforeInvoice = message.substring(0, message.indexOf('Invoice #'));
        const afterInvoice = message.substring(message.indexOf(invoiceNumber) + invoiceNumber.length);
        
        return (
          <span>
            {beforeInvoice}
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleNotificationClick(notification.id, notification.notification.invoiceId);
              }}
              className="text-accent-color-600 hover:text-accent-color-800 dark:text-accent-color-400 dark:hover:text-accent-color-300 font-medium underline"
            >
              Invoice #{invoiceNumber}
            </button>
            {afterInvoice}
          </span>
        );
      }
    }
    
    // Default message rendering
    return <span dangerouslySetInnerHTML={{ __html: sanitizeHtml(message) }} />;
  };

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">My Notifications</h1>
      <div className="bg-white dark:bg-gray-800 shadow rounded-lg">
        <ul className="divide-y divide-gray-200 dark:divide-gray-700">
          {items.length === 0 ? (
            <li className="p-4 text-center text-gray-500 dark:text-gray-400">No notifications</li>
          ) : items.map((un: any) => (
            <li 
              key={un.id} 
              className={`p-4 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer ${
                !un.isRead ? 'bg-blue-50 dark:bg-blue-900/20' : ''
              }`}
              onClick={() => handleNotificationClick(un.id, un.notification.invoiceId)}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-1">
                    {un.notification.title}
                  </div>
                  <div className="text-sm text-gray-700 dark:text-gray-300 mb-1">
                    {renderNotificationMessage(un)}
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">
                    {new Date(un.notification.createdDate).toLocaleString()}
                  </div>
                </div>
                {!un.isRead && (
                  <button 
                    className="text-sm text-accent-color-600 hover:text-accent-color-800 dark:text-accent-color-400 dark:hover:text-accent-color-300 ml-4"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleNotificationClick(un.id);
                    }}
                  >
                    Mark read
                  </button>
                )}
              </div>
            </li>
          ))}
        </ul>
        <div className="flex items-center justify-between p-4 border-t border-gray-200 dark:border-gray-700">
          <div className="text-sm text-gray-600 dark:text-gray-400">Page {page} of {totalPages}</div>
          <div className="space-x-2">
            <button 
              className="btn-secondary" 
              disabled={page<=1} 
              onClick={()=>setPage(p=>Math.max(1,p-1))}
            >
              Previous
            </button>
            <button 
              className="btn-secondary" 
              disabled={page>=totalPages} 
              onClick={()=>setPage(p=>p+1)}
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserNotificationsPage;


